import { CheckmarkCircleFilled, InfoCircleFilled } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Typography } from 'common/typography';
import { localize } from 'common/utils';
import { TextWithHighlightedMatches } from 'modules/experience-core';

import { ReportVenueSelectItem } from './types';

export type VenueRowItemProps = {
  venueSelectItem: ReportVenueSelectItem;
  searchValue?: string;
  isSelected?: boolean;
};

const VenueRowItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-self: stretch;
  width: 100%;
`;

const VenueRowItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  width: 100%;
`;

const VenueRowItemHeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  align-self: stretch;
`;

const VenueRowItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const VenueRowItemDescription = styled(Typography.Body3)<{ isSelected?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: ${cbSemanticCssVars.textSecondary};
  padding-inline: ${({ isSelected }) => (isSelected ? '20px' : 0)};
`;

const StyledCheckIcon = styled(CheckmarkCircleFilled)`
  fill: ${colors.lime100};
  width: 12px;
  height: 12px;
`;

const StyledInfoIcon = styled(InfoCircleFilled)`
  fill: ${cbSemanticCssVars.textPrimary};
  width: 12px;
  height: 12px;
`;

const TagsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TagContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const VenueRowItem = ({ venueSelectItem, searchValue = '', isSelected = false }: VenueRowItemProps) => {
  const { locale } = useIntl();

  const { name, address, city, post_code, picker_api_enabled, is_wolt_plus } = venueSelectItem;

  return (
    <VenueRowItemWrapper>
      <VenueRowItemContainer>
        <VenueRowItemHeaderContainer>
          <VenueRowItemHeader>
            {isSelected && <StyledCheckIcon />}
            <Typography.Body3>
              <TextWithHighlightedMatches text={localize(name, locale) || '-'} searchValue={searchValue} />
            </Typography.Body3>
          </VenueRowItemHeader>
          <TagsContainer>
            {picker_api_enabled && (
              <Tag variant="outlineBlack">
                <TagContainer>
                  <StyledInfoIcon />
                  <Typography.Body3>
                    <Typography.Small>
                      <FormattedMessage id="insights.reports.venues.picker-enabled" />
                    </Typography.Small>
                  </Typography.Body3>
                </TagContainer>
              </Tag>
            )}
            {is_wolt_plus && (
              <Tag variant="outlineBlack">
                <TagContainer>
                  <StyledInfoIcon />
                  <Typography.Body3>
                    <Typography.Small>
                      <FormattedMessage id="insights.reports.venues.wolt-plus" />
                    </Typography.Small>
                  </Typography.Body3>
                </TagContainer>
              </Tag>
            )}
          </TagsContainer>
        </VenueRowItemHeaderContainer>
        <VenueRowItemDescription isSelected={isSelected}>
          <Typography.Body3>
            <TextWithHighlightedMatches text={`${address.trim()}, ${post_code} ${city}`} searchValue={searchValue} />
          </Typography.Body3>
        </VenueRowItemDescription>
      </VenueRowItemContainer>
    </VenueRowItemWrapper>
  );
};
